import i18n from '../i18n';

const AlertTypes = {
  VOLTAGE: "voltage",
  CURRENT: "current",
  TEMPERATURE: "temperature",
  EMERGENCY_STOP: "emergency_stop",
  PORT_IS_INACTIVE: "port_is_inactive",
  I2C_FAILURE: "i2c_failure",
  // Encendidos y apagados del equipo (subtipo on/off/blip/firmware_restart,
  // causa en `cause`).
  POWER: "power",
  // Fallos de la nube y de la propia plataforma web.
  SERVER: "server",
  PLATFORM: "platform",
  // Un worker del firmware escalado por el supervisor.
  WORKER_ESCALATION: "worker_escalation",
  // Salud del propio equipo: disco o base local llenos, procesador caliente,
  // con subtensión o limitado (las emite el firmware).
  DISK_FULL: "disk_full",
  DB_OVERFLOW: "db_overflow",
  CPU: "cpu",
  // Relé pegado (sigue con tensión mandado a OFF) o salida sin tensión.
  RELAY: "relay",
  // Salud eléctrica del camión (batería en reposo, arranque, alternador).
  BATTERY: "battery",
  ALTERNATOR: "alternator",
  // Estado del camino: tramo que empeoró o golpe fuerte (vibración del IMU).
  VIBRATION: "vibration",
  // La nube dejó de recibir el latido del equipo (subtipo lost) o lo volvió
  // a recibir (restored). Las genera el servidor, no el equipo.
  CONNECTION: "connection",
  // Una zona del mapa de cobertura empeoró (subtipo degraded) o quedó casi
  // sin servicio (dead_zone). Las calcula la nube con las muestras de red.
  NETWORK: "network",
};

// Grupos para el filtro de la zona de alertas.
const AlertCategories = {
  DEVICE: "device",
  POWER: "power",
  SYSTEM: "system",
};

function alertCategory(type) {
  // La conexión va con energía: un equipo que deja de responder es, casi
  // siempre, un equipo que se apagó sin poder avisar.
  if (type === AlertTypes.POWER || type === AlertTypes.CONNECTION) return AlertCategories.POWER;
  if (
    type === AlertTypes.SERVER ||
    type === AlertTypes.PLATFORM ||
    type === AlertTypes.WORKER_ESCALATION ||
    type === AlertTypes.DISK_FULL ||
    type === AlertTypes.DB_OVERFLOW ||
    type === AlertTypes.CPU
  ) {
    return AlertCategories.SYSTEM;
  }
  return AlertCategories.DEVICE;
}

function powerCauseToText(cause) {
  if (!cause) return "";
  const key = `powerCause.${cause}`;
  const text = i18n.t(key);
  return text === key ? cause : text;
}

// El equipo redacta el detalle de energía en español ("Entrada 23.6 V ·
// Ignición ON · UPS inactiva", "apagado hace 2 h 5 min"...). Aquí se
// extraen los datos y se vuelven a redactar en el idioma de la interfaz.
// Si el formato no se reconoce, la sección muestra el texto tal cual.
function parsePowerMessage(message) {
  if (!message) return null;
  const m = String(message);
  const pick = (re) => {
    const r = m.match(re);
    return r ? r[1] : null;
  };
  const parsed = {
    inputV: pick(/Entrada (\d+(?:\.\d+)?) V/),
    noInput: /Entrada sin lectura/.test(m),
    ign: pick(/Ignición (ON|OFF)/),
    ups: pick(/UPS (activa|inactiva)/),
    uptime: pick(/desde hace ([^.]+?)(?:\.|$)/),
    prevAgo: pick(/apagado hace ([^,)]+)/),
    prevV: pick(/entrada entonces (\d+(?:\.\d+)?) V/),
    prevIgn: pick(/ignición entonces (ON|OFF)/),
    note: pick(/\(([A-Za-z]+Error[^)]*)\)/),
  };
  const known = ["inputV", "ign", "ups", "uptime", "prevAgo", "prevV", "prevIgn"];
  parsed.recognized = parsed.noInput || known.some((k) => parsed[k] !== null);
  return parsed;
}

function localizeDuration(d) {
  if (!d) return "";
  // El firmware usa "s", "min", "h" y "d"; solo el día cambia de letra.
  return d.replace(/(\d+) d\b/, `$1 ${i18n.t('powerDetail.dayUnit')}`);
}

function powerDetailText(message) {
  const parsed = parsePowerMessage(message);
  if (!parsed || !parsed.recognized) return null;
  const parts = [];
  if (parsed.inputV !== null) parts.push(i18n.t('powerDetail.input', { v: parsed.inputV }));
  else if (parsed.noInput) parts.push(i18n.t('powerDetail.noInput'));
  if (parsed.ign) parts.push(i18n.t(parsed.ign === "ON" ? 'powerDetail.ignOn' : 'powerDetail.ignOff'));
  if (parsed.ups) parts.push(i18n.t(parsed.ups === "activa" ? 'powerDetail.upsActive' : 'powerDetail.upsInactive'));
  const extras = [];
  if (parsed.uptime) extras.push(i18n.t('powerDetail.uptime', { d: localizeDuration(parsed.uptime) }));
  if (parsed.prevAgo) extras.push(i18n.t('powerDetail.prevShutdown', { d: localizeDuration(parsed.prevAgo) }));
  if (parsed.prevV !== null) extras.push(i18n.t('powerDetail.prevInput', { v: parsed.prevV }));
  if (parsed.prevIgn) extras.push(i18n.t('powerDetail.prevIgn', { s: parsed.prevIgn }));
  if (parsed.note) extras.push(parsed.note);
  const line = parts.join(" · ");
  return extras.length ? `${line}\n${extras.join(", ")}` : line;
}

function systemCodeToText(code) {
  if (!code) return "";
  const key = `systemCode.${code}`;
  const text = i18n.t(key);
  return text === key ? code : text;
}

const AlertSubtypes = {
  OVER: "over",
  UNDER: "under",
  OVER_12V: "12v_over",
  OVER_24V: "24v_over",
};

function alertToIcon(type) {
  if (type === AlertTypes.VOLTAGE) return "/icons/alerts/voltage.png";
  else if (type === AlertTypes.CURRENT) return "/icons/alerts/current.png";
  else if (type === AlertTypes.TEMPERATURE)
    return "/icons/alerts/temperature.png";
  else if (type === AlertTypes.EMERGENCY_STOP)
    return "/icons/alerts/emergency_stop.png";
  else if (type === AlertTypes.PORT_IS_INACTIVE)
    return "/icons/alerts/emergency_stop.png";
  else if (type === AlertTypes.I2C_FAILURE)
    return "/icons/alerts/emergency_stop.png";
  else if (type === AlertTypes.POWER) return "/icons/alerts/power.svg";
  else if (type === AlertTypes.SERVER || type === AlertTypes.PLATFORM || type === AlertTypes.CONNECTION)
    return "/icons/alerts/network.png";
  else if (type === AlertTypes.WORKER_ESCALATION)
    return "/icons/alerts/emergency_stop.png";
  else if (type === AlertTypes.NETWORK) return "/icons/alerts/network.png";
  else if (type === AlertTypes.RELAY || type === AlertTypes.BATTERY || type === AlertTypes.ALTERNATOR)
    return "/icons/alerts/voltage.png";
  else if (type === AlertTypes.CPU) return "/icons/alerts/temperature.png";
  return "/icons/alerts/alert.png";
}

// Duración de un corte de conexión (segundos que calcula la nube) en el
// formato corto del firmware: "45 s", "4 min", "2 h 5 min", "1 d 3 h".
function formatOutage(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s} s`;
  const minutes = Math.floor(s / 60);
  if (s < 3600) return `${minutes} min`;
  const hours = Math.floor(s / 3600);
  if (s < 86400) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
  const days = Math.floor(s / 86400);
  const dayUnit = i18n.t('powerDetail.dayUnit');
  return hours % 24 ? `${days} ${dayUnit} ${hours % 24} h` : `${days} ${dayUnit}`;
}

function getTemperatureScale() {
  return [
    { hex: "#EE0411", value: 70 },
    { hex: "#FB320B", value: 60 },
    { hex: "#FC7E09", value: 50 },
    { hex: "#FFD728", value: 40 },
    { hex: "#F2FD57", value: 30 },
    { hex: "#98EB81", value: 20 },
    { hex: "#1DC7A5", value: 10 },
    { hex: "#02AFD0", value: 0 },
    { hex: "#0686F3", value: -10 },
    { hex: "#113BE1", value: -20 },
  ];
}

// 999 es "sin lectura" (sensor en corto, desconectado o equipo en modo
// degradado): gris, no el rojo del tope de la escala.
const NO_TEMPERATURE_READING = 900;

function hasTemperatureReading(temperature) {
  const value = parseFloat(temperature);
  return Number.isFinite(value) && value < NO_TEMPERATURE_READING;
}

function temperatureToHex(temperature) {
  if (!hasTemperatureReading(temperature)) return "#B4C3E4";
  let scale = getTemperatureScale();
  let hex = "#113BE1";
  for (let i = 0; i < scale.length; i++) {
    if (temperature >= scale[i].value) {
      hex = scale[i].hex;
      break;
    }
  }
  return hex;
}

function numberToLetter(number) {
  switch (number) {
    case 0:
      return "A";
    case 1:
      return "B";
    case 2:
      return "C";
    case 3:
      return "D";
    case 4:
      return "E";
    case 5:
      return "F";
    case 6:
      return "G";
    case 7:
      return "H";
    case 8:
      return "I";
    case 9:
      return "J";
    default:
      return "";
  }
}

// `cause` solo importa para energía: el encendido sin apagado ordenado
// previo se titula como el apagado que el equipo no pudo reportar.
function typeToName(type, subtype = null, cause = null) {
  switch (type) {
    case AlertTypes.VOLTAGE:
      switch (subtype) {
        case AlertSubtypes.OVER:
          return i18n.t('alertType.highVoltage');
        case AlertSubtypes.UNDER:
          return i18n.t('alertType.lowVoltage');
        case AlertSubtypes.OVER_12V:
          return i18n.t('alertType.highVoltage12v');
        case AlertSubtypes.OVER_24V:
          return i18n.t('alertType.highVoltage24v');
        default:
          return "";
      }
    case AlertTypes.CURRENT:
      return i18n.t('alertType.overconsumption');
    case AlertTypes.TEMPERATURE:
      if (subtype === AlertSubtypes.UNDER) return i18n.t('alertType.lowTemperature');
      if (subtype === "sensor_fault") return i18n.t('alertType.temperatureSensorFault');
      return i18n.t('alertType.overheating');
    case AlertTypes.RELAY:
      return subtype === "stuck_on" ? i18n.t('alertType.relayStuckOn') : i18n.t('alertType.relayNoOutput');
    case AlertTypes.VIBRATION:
      return subtype === "impact" ? i18n.t('alertType.roadImpact') : i18n.t('alertType.roadDegraded');
    case AlertTypes.BATTERY:
      switch (subtype) {
        case "critical":
          return i18n.t('alertType.batteryCritical');
        case "weak_crank":
          return i18n.t('alertType.batteryWeakCrank');
        default:
          return i18n.t('alertType.batteryLow');
      }
    case AlertTypes.ALTERNATOR:
      switch (subtype) {
        case "high":
          return i18n.t('alertType.alternatorHigh');
        case "transients":
          return i18n.t('alertType.alternatorTransients');
        default:
          return i18n.t('alertType.alternatorLow');
      }
    case AlertTypes.CPU:
      switch (subtype) {
        case "temp_high":
          return i18n.t('alertType.cpuTempHigh');
        case "under_voltage":
          return i18n.t('alertType.cpuUnderVoltage');
        case "throttled":
          return i18n.t('alertType.cpuThrottled');
        default:
          return i18n.t('alertType.cpu');
      }
    case AlertTypes.DISK_FULL:
      return i18n.t('alertType.diskFull');
    case AlertTypes.DB_OVERFLOW:
      return i18n.t('alertType.dbOverflow');
    case AlertTypes.EMERGENCY_STOP:
      return i18n.t('alertType.emergencyStop');
    case AlertTypes.PORT_IS_INACTIVE:
      return i18n.t('alertType.portInactive');
    case AlertTypes.I2C_FAILURE:
      if (subtype === "No Telemetry Recovery") {
        return i18n.t('alertType.i2cNoRecovery');
      }
      return i18n.t('alertType.i2cFailure');
    case AlertTypes.POWER:
      switch (subtype) {
        case "off":
          return i18n.t('alertType.powerOff');
        case "on":
          if (cause === "unexpected") return i18n.t('alertType.powerOffUnreported');
          return i18n.t('alertType.powerOn');
        case "blip":
          return i18n.t('alertType.powerBlip');
        case "firmware_restart":
          return i18n.t('alertType.firmwareRestart');
        default:
          return i18n.t('alertType.power');
      }
    case AlertTypes.SERVER:
      return i18n.t('alertType.server');
    case AlertTypes.PLATFORM:
      return i18n.t('alertType.platform');
    case AlertTypes.WORKER_ESCALATION:
      return i18n.t('alertType.workerEscalation', { worker: subtype || "" });
    case AlertTypes.CONNECTION:
      return subtype === "restored"
        ? i18n.t('alertType.connectionRestored')
        : i18n.t('alertType.connectionLost');
    case AlertTypes.NETWORK:
      return subtype === "dead_zone"
        ? i18n.t('alertType.networkDeadZone')
        : i18n.t('alertType.networkDegraded');
    default:
      return "";
  }
}

function typeToUnit(type, subtype = null) {
  switch (type) {
    case AlertTypes.BATTERY:
      return subtype === "weak_crank" ? "V" : "%";
    case AlertTypes.ALTERNATOR:
      return subtype === "transients" ? "" : "V";
    case AlertTypes.VIBRATION:
      return subtype === "impact" ? "g" : "";
    case AlertTypes.VOLTAGE:
      return "V";
    case AlertTypes.CURRENT:
      return "A";
    case AlertTypes.TEMPERATURE:
    case AlertTypes.CPU:
      return "°C";
    case AlertTypes.RELAY:
      return "V";
    case AlertTypes.I2C_FAILURE:
      return i18n.t('unit.retries');
    default:
      return "";
  }
}

function zoneToCoods(zone, model) {
  if (model !== "lite") {
    switch (zone) {
      case 0:
        return { top: "71%", left: "27%" };
      case 1:
        return { top: "47%", left: "45%" };
      case 2:
        return { top: "89%", left: "27%" };
      case 3:
        return { top: "44%", left: "72%" };
      case 4:
        return { top: "6%", left: "49%" };
      case 5:
        return { top: "15%", left: "66%" };
      case 6:
        return { top: "8%", left: "90.5%" };
      case 7:
        return { top: "70%", left: "58%" };
      default:
        return { top: 999999, left: 999999 };
    }
  } else {
    switch (zone) {
      case 0:
        return { top: "71%", left: "27%" };
      case 1:
        return { top: "47%", left: "45%" };
      case 2:
        return { top: "89%", left: "27%" };
      case 3:
        return { top: "44%", left: "72%" };
      case 4:
        return { top: "6%", left: "49%" };
      case 5:
        return { top: "15%", left: "66%" };
      case 6:
        return { top: "8%", left: "90.5%" };
      case 7:
        return { top: "70%", left: "58%" };
      default:
        return { top: 999999, left: 999999 };
    }
  }
}

function zoneToName(zone, model) {
  if (model !== "lite") {
    switch (zone) {
      case 0:
        return i18n.t('zone.port8');
      case 1:
        return i18n.t('zone.output24v');
      case 2:
        return i18n.t('zone.port10');
      case 3:
        return i18n.t('zone.source24v');
      case 4:
        return i18n.t('zone.output12v');
      case 5:
        return i18n.t('zone.source12v');
      case 6:
        return i18n.t('zone.input');
      case 7:
        return i18n.t('zone.cpu');
      default:
        return ":(";
    }
  } else {
    switch (zone) {
      case 0:
        return i18n.t('zone.port8');
      case 1:
        return i18n.t('zone.output24v');
      case 2:
        return i18n.t('zone.port9');
      case 3:
        return i18n.t('zone.source24v');
      case 4:
        return i18n.t('zone.output12v');
      case 5:
        return i18n.t('zone.source12v');
      case 6:
        return i18n.t('zone.input');
      case 7:
        return i18n.t('zone.cpu');
      default:
        return ":(";
    }
  }
}

// Zona horaria de la empresa (página Empresa). Todas las horas de la
// plataforma se muestran en ella y no en la del navegador: quien mira desde
// Lima los equipos de una mina en Chile ve la hora de Chile, la misma que el
// equipo. Los datos llegan en UTC; esto solo cambia cómo se pintan. Sin zona
// guardada (sesión anterior al cambio) se usa la del navegador.
const COMPANY_TZ_KEY = "company_timezone";

function isValidTimezone(tz) {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch (e) {
    return false;
  }
}

function getCompanyTimezone() {
  let tz = null;
  try {
    tz = window.localStorage.getItem(COMPANY_TZ_KEY);
  } catch (e) {
    // sin storage: zona del navegador
  }
  return isValidTimezone(tz) ? tz : undefined;
}

function setCompanyTimezone(tz) {
  try {
    if (isValidTimezone(tz)) window.localStorage.setItem(COMPANY_TZ_KEY, tz);
    else window.localStorage.removeItem(COMPANY_TZ_KEY);
  } catch (e) {
    // sin storage se usa la del navegador
  }
}

// "dd/MM HH:mm" y similares (dd, MM, yyyy, HH, mm, ss) en la zona de la
// empresa. date-fns `format` usa siempre la del navegador.
function formatInCompanyTz(timestamp, pattern) {
  try {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: getCompanyTimezone(),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    const tokens = {
      yyyy: get("year"),
      MM: get("month"),
      dd: get("day"),
      HH: get("hour"),
      mm: get("minute"),
      ss: get("second"),
    };
    return pattern.replace(/yyyy|MM|dd|HH|mm|ss/g, (tok) => tokens[tok]);
  } catch (e) {
    return "";
  }
}

function formatTimestamp(timestamp) {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { timeZone: getCompanyTimezone() });
}

function formatDate(timestamp) {
  let date = new Date(timestamp);
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
}

function formatTimezoneDate(dateString) {
  return formatTimestamp(dateString);
}

function pduiIsReady(timestamp) {
  const now = new Date().getTime();
  const timestampMs = new Date(timestamp).getTime();
  const differenceMs = now - timestampMs;
  return differenceMs <= 30000;
}

function parsePortAssignment(portAssignment, outputNumber) {
  const outputPortAssignment = portAssignment[outputNumber];

  if (outputPortAssignment.length === 1) {
    return `OUT ${outputPortAssignment[0]}`;
  } else if (outputPortAssignment.length === 2) {
    return `OUT ${outputPortAssignment[0]} y OUT ${outputPortAssignment[1]}`;
  } else {
    return "OUT";
  }
}

export {
  AlertTypes,
  AlertCategories,
  alertCategory,
  powerCauseToText,
  powerDetailText,
  systemCodeToText,
  alertToIcon,
  formatOutage,
  getTemperatureScale,
  numberToLetter,
  zoneToName,
  temperatureToHex,
  formatTimestamp,
  formatInCompanyTz,
  getCompanyTimezone,
  setCompanyTimezone,
  isValidTimezone,
  typeToName,
  typeToUnit,
  zoneToCoods,
  formatTimezoneDate,
  pduiIsReady,
  parsePortAssignment,
  hasTemperatureReading,
};
